import { useCallback, useMemo, useState } from 'react';
import { Tearsheet } from '@carbon/ibm-products';
import { ProvideRootResource } from 'components/hydrate-atoms/hydrate-atoms';
import { ServerExportStatusBanner } from 'components/server-export-status-banner';
import { api } from 'lib/request/api';
import { useServerExport, type ExportParams } from 'lib/hooks/useServerExport';
import { formatNumber } from 'utils/number';
import { PackageMap } from './package-map';
import { AssetView, type MasterDataExportRequest } from './asset-view';
import { ChartExportRegistryProvider } from './chart-export-registry';
import { useEval3Page } from './context';
import { interestModelableOnlyAtom } from './atoms';
import type { AssetRow } from './types';
import styles from './asset-picker-tearsheet.module.scss';

/** Endpoint for the async server-side master-data export (Exportable envelope). */
const MASTER_DATA_EXPORT_URL = 'queries/packages/master_data/export';

/** One picked tract — the id plus the display data the host list renders. */
export interface PickedAsset {
  /** The tract id (`String(row.id)` — the table's row id). */
  id: string;
  /** Tract display name (the Name column value). */
  name: string;
  /** Instrument type (OGL / UMI) — every picker row is modelable, so one of the two. */
  instrumentType: string;
}

export interface AssetPickerTearsheetProps {
  /** Whether the tearsheet is open. The body mounts only while open. */
  open: boolean;
  /** Close (Cancel / ×): discards the pending selection. Confirm also calls this after `onAdd`. */
  onClose: () => void;
  /** Tracts already added to the host — rendered visible but disabled, never pre-checked. */
  usedAssetIds: ReadonlySet<string>;
  /** Confirm: the selected tracts (id + display data), then the picker closes. */
  onAdd: (selectedAssets: PickedAsset[]) => void;
}

/**
 * The Interest Modeling "Add properties" picker: a wide Tearsheet whose
 * body is the REAL eval3-new master-data surface — `PackageMap` over the Assets
 * view (locked to `assets`; no view switcher) with its full toolbar, server
 * pagination/sort/filters, row expansion, and the map-pin column — narrowed
 * server-side to modelable tracts (OGL/UMI) via `interestModelableOnly: true` on every
 * read.
 *
 * Isolation: the body subtree mounts under its own `ProvideRootResource` (fresh
 * Jotai Provider — spatial/dataset filters, pin, map ref, AOI state all start
 * clean and never leak into the page's atoms), hydrated with
 * `interestModelableOnlyAtom: true` so every hook inside sends the flag. The page
 * identity crosses via `Eval3PageContext` and the React Query client passes
 * through (cache keys differ via the flag). A throwaway
 * `ChartExportRegistryProvider` shields the PAGE map's content-export
 * registration — both maps register under the id `map`, so without it the
 * picker's map would clobber (and on close, unregister) the page's entry. The
 * body mounts only while open, so each open starts from clean picker-local
 * state and a closed picker fetches nothing.
 *
 * Selection: a cross-page `Map` of picked rows owned HERE (outside the Jotai
 * Provider — paging/filtering in the table never drops it), cleared on every
 * close (so a reopen starts clean with no stale-check frame); the table
 * reflects the current page's slice and reports flips (`record-picker-table`
 * wiring). Rows in `usedAssetIds` are visible but disabled via the
 * `_canSelect: false` row sentinel.
 *
 * @param {AssetPickerTearsheetProps} props - Open state, close/confirm handlers, and the already-added ids.
 * @return {JSX.Element} The picker tearsheet.
 */
export function AssetPickerTearsheet({
  open,
  onClose,
  usedAssetIds,
  onAdd,
}: AssetPickerTearsheetProps) {
  const { packageId, calculationId } = useEval3Page();

  // Server-side export lives HERE, not in AssetView: the tearsheet is always
  // mounted (only its BODY is gated by `open`), so the Pusher-delivered download
  // survives the picker closing — including the "Download then Add property"
  // confirm flow that closes it. Mirrors the eval3-new content-export owner.
  // AssetView (inside the body's Provider) assembles the request body and hands it
  // to `queueExport`; the file auto-opens when the completion event arrives.
  const exportFn = useCallback(async (params?: ExportParams) => {
    const res = await api
      .post(MASTER_DATA_EXPORT_URL, {
        json: params?.body as MasterDataExportRequest,
      })
      .json<{ exportId: string }>();
    return { exportId: res.exportId };
  }, []);

  const { startExport, exportStatus, resetExport } = useServerExport({
    channel: `package_${packageId}`,
    event: 'master_data_export_complete',
    exportFn,
  });

  const queueExport = useCallback(
    (body: MasterDataExportRequest) => {
      void startExport({ body });
    },
    [startExport]
  );

  // A Map (not a Set) so Confirm can hand the host the display data (name +
  // OGL/UMI) without re-fetching rows it may have paged away from.
  const [selected, setSelected] = useState<ReadonlyMap<string, PickedAsset>>(
    new Map()
  );

  // Every close path (Cancel, the header ×, post-confirm) clears the pending
  // selection SYNCHRONOUSLY before closing, so a reopen can never render a frame
  // of stale checks (clearing in an on-open effect would). Cancel/close discards
  // — the pending map simply never reaches `onAdd`.
  const handleClose = useCallback(() => {
    setSelected(new Map());
    onClose();
  }, [onClose]);

  const handleToggle = useCallback((rowId: string, row: AssetRow) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.set(rowId, {
          id: rowId,
          name: row.name ?? '',
          instrumentType: row.instrumentType ?? '',
        });
      }
      return next;
    });
  }, []);

  const selectedIds = useMemo(() => new Set(selected.keys()), [selected]);

  const count = selected.size;
  const confirmLabel =
    count > 1
      ? `Add ${formatNumber({ value: count, minPrecision: 0 })} properties`
      : 'Add property';

  const handleConfirm = () => {
    // The host only needs the `{ id, name, instrumentType }` objects — it derives
    // the ids it needs from them — so hand over just the assets.
    onAdd([...selected.values()]);
    handleClose();
  };

  return (
    <Tearsheet
      open={open}
      onClose={handleClose}
      className={styles.tearsheet}
      label="Interest modeling"
      title="Add properties"
      hasCloseIcon
      closeIconDescription="Close"
      actions={[
        {
          key: 'cancel',
          kind: 'secondary',
          label: 'Cancel',
          onClick: handleClose,
        },
        {
          key: 'add',
          kind: 'primary',
          label: confirmLabel,
          disabled: count === 0,
          onClick: handleConfirm,
        },
      ]}
    >
      {open && (
        <ChartExportRegistryProvider>
          <ProvideRootResource
            initialValues={[[interestModelableOnlyAtom, true]]}
          >
            <div className={styles.body}>
              {/* Export lifecycle feedback (Exporting… / ready / failed). Renders
                  null when idle, so no layout impact until an export runs.
                  ponytail: only visible while the picker is open — a failure whose
                  Pusher event lands AFTER the picker closes is silent. Upgrade to
                  a page-level toast (extract content-export's showToast) if that
                  gap matters. */}
              <ServerExportStatusBanner
                status={exportStatus}
                onDismiss={resetExport}
              />
              <div className={styles.mapRegion}>
                <PackageMap />
              </div>
              <div className={styles.tableRegion}>
                <AssetView
                  packageId={packageId}
                  calculationId={calculationId}
                  viewDropdown={null}
                  picker={{
                    selectedIds,
                    usedAssetIds,
                    onToggle: handleToggle,
                    onExport: queueExport,
                  }}
                />
              </div>
            </div>
          </ProvideRootResource>
        </ChartExportRegistryProvider>
      )}
    </Tearsheet>
  );
}
